import React, { useCallback } from 'react';
import { SlateReactPresentation } from 'slate-react-presentation';

import Quote from '@fec/frontend/modules/content/quote/quote.js';
import LivetickerItem from '@fec/frontend/modules/liveticker/LivetickerItem/LivetickerItem.jsx';
import { HtmlEmbed } from '@fec/frontend/modules/content/embed/HtmlEmbed.js';
import { PrivacyShield } from '@fec/frontend/modules/content/embed/PrivacyShield.js';
import { TwitterEmbed } from '@fec/frontend/modules/content/embed/TwitterEmbed.js';
import { FacebookEmbed } from '@fec/frontend/modules/content/embed/FacebookEmbed.js';
import { InstagramEmbed } from '@fec/frontend/modules/content/embed/InstagramEmbed.js';
import RelatedItemList from '@fec/frontend/modules/groups-and-lists/related-items-list/RelatedItemsList.jsx';
import { ElectionWidget } from '@fec/frontend/snowflakes/article/election-widget/ElectionWidget.jsx';
import ImageFigure from '@fec/frontend-aron/modules/media/ImageFigure/ImageFigure.jsx';
import BasicVideo from '@fec/frontend-aron/modules/media/BasicVideo/BasicVideo.jsx';
import InlineAudio from '@fec/frontend-aron/modules/media/InlineAudio/InlineAudio.jsx';
import BasicVideoApp from '@fec/frontend-aron/modules/media/BasicVideoApp/BasicVideoApp.jsx';
import InlineAudioApp from '@fec/frontend-aron/modules/media/InlineAudioApp/InlineAudioApp.jsx';
import PersonBox from '@fec/frontend-aron/modules/content/PersonBox/PersonBox.jsx';

export const TickerItem = ({
  title,
  date,
  slateJson,
  isFocussed = false,
  isWebview = false,
}) => {
  const FB_APP_ID = '1445627839073129';
  const FB_CLIENT_TOKEN = 'ea0685dd31edf4798e85f47ae80e10e4';
  const renderElement = useCallback(({ attributes, children, element }) => {
    switch (element.type) {
      case 'h1':
        return <h1 {...attributes}>{children}</h1>;
      case 'link':
        return (
          <a
            href={element.link}
            target={element.variety === 'external' ? '_blank' : '_self'}
            rel="noreferrer">
            {children}
          </a>
        );
      case 'numberList':
        return <ol {...attributes}>{children}</ol>;
      case 'bulletList':
        return <ul {...attributes}>{children}</ul>;
      case 'listItem':
        return <li {...attributes}>{children}</li>;
      case 'heading':
        return <h2 {...attributes}>{children}</h2>;
      case 'quote':
        return <Quote {...element} />;
      case 'image':
        if (element.hasOwnProperty('provider')) {
          return (
            <div className="liveticker-item__element liveticker-item__element--full-width-on-mobile">
              <ImageFigure image={{ ...element, id: element.imageId }} />
            </div>
          );
        }

        break;
      case 'twitter':
        return (
          <div className="liveticker-item__element">
            <PrivacyShield title={element.title}>
              <TwitterEmbed url={element.url} />
            </PrivacyShield>
          </div>
        );
      case 'facebook':
        return (
          <div className="liveticker-item__element">
            <PrivacyShield title={element.title}>
              <FacebookEmbed url={element.url} />
            </PrivacyShield>
          </div>
        );
      case 'instagram':
        return (
          <div className="liveticker-item__element">
            <PrivacyShield title={element.title}>
              <InstagramEmbed
                url={element.url}
                clientAccessToken={`${FB_APP_ID}|${FB_CLIENT_TOKEN}`}
                maxWidth={320}
                hideCaption={false}
                containerTagName="div"
                protocol=""
                injectScript
                onLoading={() => {}}
                onSuccess={() => {}}
                onAfterRender={() => {}}
                onFailure={(err) => {
                  // eslint-disable-next-line no-console
                  console.error('Instagram Embed Failure', err);
                }}
              />
            </PrivacyShield>
          </div>
        );
      case 'html':
        return (
          <div className="liveticker-item__element">
            <PrivacyShield
              title={element.title}
              excludeFromCmp={element.excludeFromCmp}>
              <HtmlEmbed {...element}></HtmlEmbed>
            </PrivacyShield>
          </div>
        );
      case 'video':
        return isWebview ? (
          <div className="liveticker-item__element liveticker-item__element--full-width-on-mobile">
            <BasicVideoApp asset={element} />
          </div>
        ) : (
          <div className="liveticker-item__element liveticker-item__element--full-width-on-mobile">
            <BasicVideo asset={element} />
          </div>
        );
      case 'audio':
        return isWebview ? (
          <div className="liveticker-item__element liveticker-item__element--full-width-on-mobile">
            <InlineAudioApp asset={element} />
          </div>
        ) : (
          <div className="liveticker-item__element liveticker-item__element--full-width-on-mobile">
            <InlineAudio asset={element} />
          </div>
        );
      case 'article':
        return (
          <div className="liveticker-item__element liveticker-item__element--outdent">
            <RelatedItemList items={[element]} currentArticleId={4711} />
          </div>
        );
      case 'electionWidget':
        return (
          <div className="liveticker-item__element">
            <ElectionWidget teaser={element.electionWidget}></ElectionWidget>
          </div>
        );
      case 'people':
        return <PersonBox person={element} />;
      default:
        return <p {...attributes}>{children}</p>;
    }
  }, []);

  const renderLeaf = useCallback(({ attributes, children, leaf }) => {
    if (leaf.bold) {
      children = <b>{children}</b>;
    }

    if (leaf.italic) {
      children = <em>{children}</em>;
    }

    return <span {...attributes}>{children}</span>;
  }, []);

  return (
    <LivetickerItem {...{ title, date, isFocussed }}>
      <SlateReactPresentation
        value={slateJson}
        renderElement={renderElement}
        renderLeaf={renderLeaf}
      />
    </LivetickerItem>
  );
};
